import { Tag, Tooltip } from '@chakra-ui/react';
import { SegmentVerdict, UnresolvedReason } from '@pelicargo/types';

type Props = {
  verdict: SegmentVerdict | null | undefined;
  unresolved: UnresolvedReason | null | undefined;
};

/**
 * How one confirmed flight lines up with the quote. Colour carries the reviewer's workload, not the sentiment: green
 * needs no attention, purple fills what the quote is missing, orange and blue change the request (a moved time, a
 * rerouted leg, a flight we never quoted), red is the one where the email itself was unreadable and only a person can
 * settle it.
 */
const VERDICT_LABELS: Record<SegmentVerdict, { label: string; colorScheme: string; help: string }> = {
  confirms: {
    label: 'On quote',
    colorScheme: 'green',
    help: 'Same flight and departure time as the quote already holds.',
  },
  'schedule-change': {
    label: 'Time changed',
    colorScheme: 'orange',
    help: 'Same flight on the same day, but the carrier has moved the departure time.',
  },
  'fills-blank': {
    label: 'Fills missing details',
    colorScheme: 'purple',
    help: 'The quote holds this leg without its schedule — by routing alone, or by flight number with no times — and this confirmation supplies what is missing.',
  },
  reroutes: {
    label: 'Reroutes leg',
    colorScheme: 'orange',
    help: 'The quote holds this flight number with no schedule, but between different airports. Nothing is booked on the leg yet, so an apply moves it to the airports the carrier confirmed and fills the times.',
  },
  'not-on-quote': {
    label: 'New flight',
    colorScheme: 'blue',
    help: 'This flight is not on the quote — the carrier booked something we had not quoted.',
  },
  unresolved: {
    label: 'Needs review',
    colorScheme: 'red',
    help: 'The email did not say enough to identify this flight, so it was not compared.',
  },
};

const UNRESOLVED_HELP: Record<UnresolvedReason, string> = {
  'no-flight-number': 'The email gives no flight number for this leg.',
  'no-date': 'The email gives no departure date for this leg.',
  'ambiguous-date': 'The date could be read two ways (day/month or month/day) and was not guessed.',
  'unparseable-date': 'The departure date was not in a form we could read.',
};

export const VerdictTag = ({ verdict, unresolved }: Props) => {
  if (!verdict) {
    return (
      <Tooltip label="This confirmation is not linked to a quote, so there is nothing to compare against.">
        <Tag size="sm" colorScheme="gray">
          Not compared
        </Tag>
      </Tooltip>
    );
  }

  const { label, colorScheme, help } = VERDICT_LABELS[verdict];
  const detail = verdict === 'unresolved' && unresolved ? UNRESOLVED_HELP[unresolved] : help;

  return (
    <Tooltip label={detail}>
      <Tag size="sm" colorScheme={colorScheme}>
        {label}
      </Tag>
    </Tooltip>
  );
};
